/**
 * Papéis e permissões (seção 6 do documento de arquitetura).
 *
 * Esta tabela é a ÚNICA fonte da verdade sobre o que cada papel pode fazer.
 * O servidor (API e Sync) aplica estas regras; o cliente apenas as usa para
 * ajustar a interface — nunca para decidir acesso.
 */
export const ROLES = ["owner", "editor", "driver", "navigator", "mentor", "viewer"] as const;
export type Role = (typeof ROLES)[number];

export type Permission =
  | "read" // ler código
  | "write" // editar o texto compartilhado
  | "suggest" // propor mudanças (modo sugestão)
  | "terminal" // ter o próprio sandbox/terminal
  | "invite" // criar convites e gerenciar membros
  | "approve" // aprovar sugestões
  | "manage" // configurações e exclusão do projeto
  | "ai" // usar a IA (Claude, Copilot) no projeto — todos menos visualizadores
  | "copy"; // copiar o código (área de transferência, arrastar) — todos menos visualizadores

const MATRIX: Record<Role, ReadonlySet<Permission>> = {
  owner: new Set(["read", "write", "suggest", "terminal", "invite", "approve", "manage", "ai", "copy"]),
  editor: new Set(["read", "write", "suggest", "terminal", "ai", "copy"]),
  driver: new Set(["read", "write", "suggest", "terminal", "ai", "copy"]),
  navigator: new Set(["read", "suggest", "terminal", "ai", "copy"]),
  mentor: new Set(["read", "suggest", "terminal", "approve", "ai", "copy"]),
  // Visualizador: só lê e conversa com o time (sem IA, terminal nem cópia do código).
  viewer: new Set(["read"]),
};

export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return MATRIX[role]?.has(permission) ?? false;
}

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

export const WRITE_ROLES: readonly Role[] = ROLES.filter((r) => can(r, "write"));

/**
 * Papéis que podem ser atribuídos (dono só por convite direto). "Motorista" saiu das opções: quem
 * digita numa sessão em par é um editor; o papel continua aceito para dados antigos.
 */
export const INVITABLE_ROLES = ["editor", "navigator", "mentor", "viewer"] as const satisfies readonly Role[];
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Dono",
  editor: "Editor",
  driver: "Motorista",
  navigator: "Navegador",
  mentor: "Mentor",
  viewer: "Visualizador",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: "Controle total: edita, convida, aprova sugestões e gerencia o projeto.",
  editor: "Edita o código e roda o próprio terminal.",
  driver: "Está com o teclado na programação em par. Edita o código.",
  navigator: "Acompanha e propõe mudanças como sugestões, sem editar diretamente.",
  mentor: "Sugere mudanças e aprova sugestões de outras pessoas.",
  viewer: "Somente leitura. Não edita e não tem terminal.",
};

/** Papéis que aparecem na escolha de permissão de um membro (sem "motorista"). */
export const ASSIGNABLE_ROLES = [
  "owner",
  "editor",
  "navigator",
  "mentor",
  "viewer",
] as const satisfies readonly Role[];
